import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, m as motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Send, Sparkles, X } from 'lucide-react';
import { toast, useCopilot } from '@/stores/ui';
import { copilotService, toAiHistory, type CopilotAnswer } from '@/services/copilot';
import { AnswerView } from './AnswerView';
import { useDialogA11y } from '@/lib/useDialogA11y';

interface ChatEntry {
  id: number;
  who: 'user' | 'copilot';
  text?: string;
  answer?: CopilotAnswer;
}

/** Copiloto VaulTex (VLT-308 v1): responde con DATOS REALES del tenant vía
 *  intenciones deterministas — §7216 verificado, cada consulta auditada en
 *  ai_conversations. Jamás improvisa ni da conclusiones fiscales. */
export function Copilot() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { open, setOpen } = useCopilot();
  const [chat, setChat] = useState<ChatEntry[]>([]);
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const idRef = useRef(0);
  const logRef = useRef<HTMLDivElement>(null);
  // Panel no-modal: Escape cierra y el foco vuelve al FAB, sin atrapar Tab.
  const dialogRef = useDialogA11y<HTMLElement>(open, () => setOpen(false), { trap: false });

  // ⌘J / Ctrl+J toggles the copilot
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'j') {
        e.preventDefault();
        setOpen(!useCopilot.getState().open);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setOpen]);

  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight });
  }, [chat]);

  const ask = async (q: string) => {
    const question = q.trim();
    if (!question || busy) return;
    setPrompt('');
    setBusy(true);
    setChat((c) => [...c, { id: ++idRef.current, who: 'user', text: question }]);
    // Memoria para Loruel (VLT-384): sus turnos previos viajan con la pregunta
    try {
      const answer = await copilotService.ask(question, toAiHistory(chat));
      setChat((c) => [...c, { id: ++idRef.current, who: 'copilot', answer }]);
    } catch {
      // La pregunta vuelve al campo para reintentar; nunca queda «pensando».
      toast(t('errors.generic.what'), 'info');
      setPrompt((p) => p || question);
    } finally {
      setBusy(false);
    }
  };

  const renderAnswer = (a: CopilotAnswer) => (
    <AnswerView answer={a} onOpenProfile={(clientId) => { setOpen(false); navigate(`/clients/${clientId}`); }} />
  );

  return (
    <>
      {/* FAB (computadora; en celular lo abre MobileActions) */}
      <motion.button
        type="button"
        aria-label={t('copilot.title')}
        onClick={() => setOpen(!open)}
        whileTap={{ scale: 0.94 }}
        whileHover={{ y: -2, scale: 1.03 }}
        className="focus-ring fixed bottom-6 right-6 z-[60] hidden h-12 w-12 place-items-center rounded-2xl bg-brand-gradient text-white shadow-glow lg:grid"
      >
        <span className="absolute inset-0 pointer-events-none animate-ping-slow rounded-2xl bg-primary/25" aria-hidden />
        <Sparkles className="relative h-5 w-5" />
      </motion.button>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.section
            ref={dialogRef}
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 340, damping: 30 }}
            role="dialog"
            aria-label={t('copilot.title')}
            className="glass-strong fixed bottom-40 right-4 z-[60] flex max-h-[70dvh] w-[min(24rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-2xl lg:bottom-24 lg:right-6"
          >
            <header className="flex items-center gap-2.5 border-b border-border/60 bg-primary/10 px-4 py-3">
              <span className="grid h-8 w-8 place-items-center rounded-xl bg-brand-gradient text-white">
                <Sparkles className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-display text-sm font-semibold">{t('copilot.title')}</p>
                <p className="truncate text-[11px] text-muted-foreground">{t('copilot.disclaimer')}</p>
              </div>
              <button
                type="button"
                aria-label={t('drawer.close')}
                onClick={() => setOpen(false)}
                className="focus-ring rounded-lg p-1.5 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </header>

            <div ref={logRef} className="flex-1 space-y-3 overflow-y-auto p-4">
              {chat.length === 0 && (
                <>
                  <p className="rounded-xl border border-border/60 bg-muted/30 px-3.5 py-3 text-sm leading-relaxed text-muted-foreground">
                    {t('copilot.v2.intro')}
                  </p>
                  <div className="grid gap-2">
                    {[t('copilot.v2.exampleMissing'), t('copilot.v2.exampleSummary')].map((ex) => (
                      <button key={ex} type="button" onClick={() => void ask(ex)}
                        className="focus-ring rounded-xl border border-border/60 bg-card/40 px-3.5 py-2.5 text-left text-sm font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground">
                        {ex}
                      </button>
                    ))}
                  </div>
                </>
              )}
              {chat.map((m) => (
                <div key={m.id} className={m.who === 'user' ? 'ml-8 rounded-xl bg-primary/12 px-3.5 py-2.5' : 'mr-4 rounded-xl border border-border/60 bg-muted/20 px-3.5 py-2.5'}>
                  {m.who === 'user' ? <p className="text-sm">{m.text}</p> : m.answer ? renderAnswer(m.answer) : null}
                </div>
              ))}
              {busy && <p className="text-xs text-muted-foreground">{t('copilot.results.working')}</p>}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                void ask(prompt);
              }}
              className="flex gap-2 border-t border-border/60 p-3"
            >
              <input
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={t('copilot.placeholder')}
                aria-label={t('copilot.placeholder')}
                className="focus-ring w-full rounded-xl border border-border/60 bg-muted/30 px-3.5 py-2.5 text-sm outline-none placeholder:text-muted-foreground"
              />
              <button
                type="submit"
                aria-label={t('copilot.send')}
                className="focus-ring grid w-11 flex-none place-items-center rounded-xl bg-brand-gradient text-white shadow-glow"
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
          </motion.section>
        )}
      </AnimatePresence>
    </>
  );
}
